<template>
    <div>
        <div v-if="!isInMatchDetail" class="match-time JARVIS_MatchCard_Img_Title notranslate">
            <div
                v-if="!liveStatus.onLive && (liveStatus.tvLive || liveStatus.sboLive)"
                class="match-livetag notranslate"
                v-bind:class="{ tvLive: liveStatus.tvLive }"
            >
                {{ getTranslationBySite('live').toUpperCase() }}
            </div>
            <SvgIcon v-if="isComponentInPp" v-bind:iconName="iconName" />
            <div v-if="liveStatus.onLive" class="match-live">
                {{ getTranslationBySite('live').toUpperCase() }}
            </div>
            <div
                v-if="!liveStatus.onLive && liveStatus.isToday && (isMyMatchPage || isComponentInPp)"
                class="match-todaytag"
            >
                {{ i18n.t('oddsDisplay_matchInfo_header_todayTag') }}
            </div>

            <div class="match-info-leagueName" v-bind:class="{ _withPromotionBadge: isPromotionBadgeShown }">
                {{ leagueName }}
            </div>
            <ScorePredictionPromotionBadge
                v-if="isPromotionBadgeShown"
                class="match-info-promotionBadge"
                gaPage="MatchCard"
                gaLabel="League"
            />
            <div
                class="match-info-refresh JARVIS_MatchCard_Btn_Refresh"
                v-on:click="refresh(true)"
            >
                <Icons
                    class="iconsSize-18"
                    v-bind:class="{ 'icons-rotated': isRefreshing }"
                    iconName="systemIcon_reload"
                />
            </div>
        </div>

        <div
            v-if="!isOutright"
            class="match-info"
            v-bind:class="['notranslate', gtmForMatchInfo]"
        >
            <div v-if="isInMatchDetail && !liveStatus.onLive && !isFromApp" class="match-info-data">
                <span v-if="info.showTime">
                    {{ info.eventDate }} {{ formatShowTime(info.showTime, info.showTimeType) }}
                </span>
                <span v-else>
                    &nbsp;
                </span>
            </div>

            <div
                class="match-info-team"
                v-bind:data-testid="`match-info-team-${info.eventId}`"
                v-bind:class="{ _appView: isFromApp }"
                v-on:click="!isInMatchDetail && goToMatchDetail(showVideoFunction)"
            >
                <div
                    class="match-info-team-name JARVIS_MatchCard_Img_TeamNameA"
                    v-bind:class="{
                        _favorite: info.isHomeFavorite,
                        _underdog: !info.isHomeFavorite,

                    }"
                >
                    {{ homeTeamName }}
                    <FoulCard
                        v-if="info.isLive && info.mainMarketEventResult.extraInfo"
                        v-bind:cardNumber="info.mainMarketEventResult.extraInfo.homeRedCards"
                    />
                </div>
                <template v-if="info && !info.isClosed">
                    <template v-if="liveStatus.onLive && !isOutright">
                        <div v-if="isShowCountdown" class="matchInfoCountdown">
                            <SboIcon
                                name="system:timer"
                                v-bind:size="16"
                            />
                            <div class="time">
                                <span
                                    v-if="isShowCountdownHours"
                                    class="digit"
                                >{{ countdownDisplay.hours }}</span>
                                <span v-if="isShowCountdownHours">:</span>
                                <span class="digit">{{ countdownDisplay.minutes }}</span><span>:</span><span class="digit">{{ countdownDisplay.seconds }}</span>
                            </div>
                        </div>
                        <MatchInfoScore
                            v-else
                            v-bind:matchInfo="info"
                            v-bind:isInMatchDetail="isInMatchDetail"
                            v-bind:showLiveStreamGifIcon="showLiveStreamGifIcon"
                        />
                    </template>
                    <div v-else-if="!isInMatchDetail && !liveStatus.onLive" class="date _test">
                        {{ info.eventDate }}
                        <time v-if="info.showTime">{{ formatShowTime(info.showTime, info.showTimeType) }}</time>
                    </div>

                    <div
                        v-else-if="!isOutright && !isFromApp"
                        class="match-info-addcalendar JARVIS_MatchCard_Btn_AddToCalendar"
                        v-on:click="addToCalendar(info)"
                    >
                        <Icons class="iconsSize-28" iconName="systemIcon_addToCalendar" />
                    </div>

                    <div
                        v-if="isInMatchDetail && !liveStatus.onLive && isFromApp"
                        class="match-info-data _appView"
                    >
                        <span v-if="info.showTime">
                            {{ info.eventDate }} {{ formatShowTime(info.showTime, info.showTimeType) }}
                        </span>
                        <span v-else>
                            &nbsp;
                        </span>
                    </div>
                </template>

                <!-- AWAY TEAM -->
                <div
                    class="match-info-team-name JARVIS_MatchCard_Img_TeamNameB"
                    v-bind:class="{
                        _favorite: info.isAwayFavorite,
                        _underdog: !info.isAwayFavorite,
                    }"
                >
                    {{ awayTeamName }}
                    <FoulCard
                        v-if="info.isLive && info.mainMarketEventResult.extraInfo"
                        v-bind:cardNumber="info.mainMarketEventResult.extraInfo.awayRedCards"
                        v-bind:isAway="true"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onMounted, onUnmounted, ref, toRefs } from 'vue';
    import { useRoute } from 'vue-router';
    import SboIcon from '@/components/common/SboIcon.vue';
    import { useLiveStream } from '@/components/liveStream/useLiveStream';
    import FoulCard from '@/components/match/FoulCard.vue';
    import MatchInfoScore from '@/components/match/matchInfo/MatchInfoScore.vue';
    import { useMatchFunction } from '@/components/matchFunction/useMatchFunction';
    import ScorePredictionPromotionBadge from '@/components/promotion/ScorePredictionPromotionBadge.vue';
    import type { IMatch } from '@/interface/IMatch';
    import { MarketPage, MatchFunction, RoutePage } from '@/interface/enum';
    import { useApp } from '@/libraries/composables/useApp';
    import { useEventCountdown } from '@/libraries/composables/useEventCountdown';
    import { useScorePredictionPromotion } from '@/libraries/composables/useScorePredictionPromotion';
    import useScroll from '@/libraries/composables/useScroll';
    import { addToCalendar } from '@/libraries/helpers/calendarHelper';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { getSportIconName } from '@/libraries/helpers/iconHelper';
    import { formatShowTime } from '@/libraries/helpers/showTimeHelper';
    import { isSoccer } from '@/libraries/helpers/sportsHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { useProductPlatformStore } from '@/stores/productPlatformStore';
    import Icons from '@/svg/Icons.vue';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'MatchInfo',
        components: {
            ScorePredictionPromotionBadge,
            FoulCard,
            MatchInfoScore,
            Icons,
            SboIcon,
            SvgIcon,
        },
        props: {
            info: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            marketPageType: {
                type: String as PropType<MarketPage>,
                default: MarketPage.Unknown,
            },
            matchName: {
                type: String,
                default: ' vs ',
            },
        },
        setup(props) {
            const { info } = toRefs(props);
            const { goToMatchDetail } = useMatchFunction(info);
            const { isLiveStreamPlayable } = useLiveStream(info);

            const leagueName = computed(() => getTranslated(info.value.leagueName));

            const homeTeamName = computed(() => getTranslated(info.value.homeTeam?.teamName) || props.matchName.split(' vs ')[0]);

            const awayTeamName = computed(() => getTranslated(info.value.awayTeam?.teamName) || props.matchName.split(' vs ')[1]);

            onMounted(() => {
                const { initHeaderDisplayHeight } = useScroll();
                initHeaderDisplayHeight(() => document.getElementById('genericHeader'), 'genericHeaderHeight');
            });

            const iconName = computed(() => getSportIconName(info.value.sportType));

            const { isComponentInPp } = storeToRefs(useProductPlatformStore());

            const ticks = ref(0);
            const isRefreshing = ref(false);

            const refreshDuration = computed(() => (info.value.isLive ? 30 : 60));

            const liveStatus = computed(() => ({
                tvLive: info.value.isTvLive,
                sboLive: info.value.isSboLive,
                onLive: info.value.isLive,
                isToday: props.marketPageType === MarketPage.Today,
            }));

            const isOutright = computed(() => props.marketPageType === MarketPage.Outright);

            const { isPromotionLeague } = useScorePredictionPromotion();
            const isPromotionBadgeShown = computed(() => !isOutright.value && isPromotionLeague(info.value.leagueName));
            const { isFromApp } = useApp();

            const route = useRoute();
            const isInMatchDetail = computed(() => route.name === RoutePage.MatchDetail);
            const isMyMatchPage = computed(() => route.name === RoutePage.MyMatches);

            const { countdownDisplay, isShowCountdownHours, isShowCountdown } = useEventCountdown(info);

            const showLiveStreamGifIcon = computed(() => {
                if (isOutright.value || isInMatchDetail.value) return false;

                return (info.value.isLive || (isSoccer(info.value.sportType) && !info.value.isSimulation)) && isLiveStreamPlayable.value;
            });

            const gtmForMatchInfo = computed(() => {
                if (isInMatchDetail.value) {
                    return '';
                }
                if (showLiveStreamGifIcon.value) {
                    return 'JARVIS_MatchCard_Section_MacthInfoWithLiveStreaming';
                }
                return 'JARVIS_MatchCard_Section_MacthInfo';
            });

            const showVideoFunction = computed(() => ({ matchFunctionTab: showLiveStreamGifIcon.value ? MatchFunction.LiveStream : MatchFunction.Unknown }));

            function tick() {
                ticks.value++;
                if (ticks.value >= refreshDuration.value) {
                    refresh();
                }
            }

            function refresh(force: boolean = false) {
                if (isRefreshing.value) return;

                ticks.value = 0;

                if (force) {
                    realRefresh();
                } else {
                    fakeRefresh();
                }
            }

            function fakeRefresh() {
                refreshAnimation();
            }

            function realRefresh() {
                refreshAnimation();

                if (isOutright.value) {
                    window.eventEmitter.emit('refreshMatch', info.value.leagueId, true, info.value.isLive);
                } else {
                    window.eventEmitter.emit('refreshMatch', info.value.eventId, true);
                }
            }

            function refreshAnimation() {
                isRefreshing.value = true;
                setTimeout(() => {
                    isRefreshing.value = false;
                }, 1000);
            }

            function triggerRefreshAnimation(eventId: number) {
                if (eventId === info.value.eventId) {
                    refreshAnimation();
                }
            }

            onMounted(() => {
                window.eventEmitter.on('tick', tick);
                window.eventEmitter.on('triggerRefreshAnimation', triggerRefreshAnimation);
            });

            onUnmounted(() => {
                window.eventEmitter.off('tick', tick);
                window.eventEmitter.off('triggerRefreshAnimation', triggerRefreshAnimation);
            });

            return {
                i18n,
                getTranslationBySite,
                goToMatchDetail,
                formatShowTime,
                addToCalendar,
                isLiveStreamPlayable,
                iconName,
                leagueName,
                homeTeamName,
                awayTeamName,
                isOutright,
                isPromotionBadgeShown,
                showVideoFunction,
                isMyMatchPage,
                liveStatus,
                gtmForMatchInfo,
                refresh,
                isComponentInPp,
                isInMatchDetail,
                showLiveStreamGifIcon,
                isRefreshing,
                isFromApp,
                countdownDisplay,
                isShowCountdown,
                isShowCountdownHours,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/match";
</style>
